import React from 'react';
import { graphql, PageProps } from 'gatsby';
import { Container } from '@printdeal/ui-library';
import SEO from '../components/Seo';
import Layout from '../components/Layout';
import Breadcrumb from '../components/Breadcrumb';
import CategoryPage from '../components/product-category/CategoryPage';
import type { ProductListingPage } from '../types/product/productListingPage';
import type { IPageContext } from '../types/gatsby';
import '../css/product-category-page.css';

export const query = graphql`
  fragment FeaturedCardImage on ContentfulAsset {
    id: contentful_id
    title
    url
    width
    height
  }
  fragment ProductDetailPage on ContentfulProductContent {
    __typename
    id: contentful_id
    featuredImage {
      title
      url
    }
    productDiscounts
    productReviews(locale: $locale)
    image: getGalleryImage(position: 1)
    path
    productName
    productCategory {
      categoryName
    }
    description: featuredDescription
    selectorAttributeValuesByName(attributeName: "Color")
  }
  fragment AlternativeImage on ContentfulAlternativeImage {
    __typename
    id: contentful_id
    alternativeImage: image {
      title
      url
    }
    productDetailPage {
      ...ProductDetailPage
    }
  }

  query ($locale: String!, $contentful_id: String!) {
    productCategory: contentfulSiteCategory(
      contentful_id: {eq: $contentful_id}
      node_locale: {eq: $locale}
    ) {
      node_locale
      paths
      id
      contentful_id
      title
      slug
      categoryName
      shopInShopContent {
        bottomBanner {
          ... on ContentfulModuleFullWidth {
            ...ModuleFullWidth
          }
        }
        topBanner {
          ... on ContentfulModuleFullWidth {
            ...ModuleFullWidth
          }
          ... on ContentfulInteractiveImage {
            __typename
            image {
              bannerURL
              hotspots {
                id
                title
                content
                featuredImageURL
                url
                percentageX
                percentageY
              }
            }
            imageMobile {
              bannerURL
              hotspots {
                id
                title
                content
                featuredImageURL
                url
                percentageX
                percentageY
              }
            }
          }
        }
        linkedSupportArticles {
          ... on ContentfulBlogPost {
            title
            path
            featuredImage {
              ...ContentfulCardImage
            }
            featuredDescription {
              featuredDescription
            }
          }
          ... on ContentfulSupportArticlePage {
            title
            path
            featuredImage {
              ...ContentfulCardImage
            }
            featuredDescription {
              featuredDescription
            }
          }
        }
        linkedFaqs {
          ...LinkedFaq
        }
        contentMarketingText: childContentfulShopInShopContentContentMarketingTextTextNode {
          childMarkdownRemark {
            rawMarkdownBody
          }
        }
      }
      introduction {
        introduction
      }
      secondaryTitle
      content {
        content
      }
      productsTitle
      seoTitle
      seoDescription {
        seoDescription
      }
      seoRobots {
        seoRobots
      }
      openGraphImage {
        ...OpenGraphImage
      }
      products {
        ... on Node {
          ... on ContentfulProductListingGroup {
            __typename
            id: contentful_id
            title
            description
            backgroundColor
            readMoreLinkText
            readMoreLink {
              ... on ContentfulBlogPost {
                path
              }
              ... on ContentfulPageContainer {
                path
              }
              ... on ContentfulSiteCategory {
                path
              }
            }
            products {
              ...ProductDetailPage
              ... on ContentfulAlternativeImage {
                ...AlternativeImage
              }
            }
          }
          ... on ContentfulProductContent {
            ...ProductDetailPage
          }
          ... on ContentfulAlternativeImage {
            ...AlternativeImage
          }
        }
      }
      contentful_id
      categories {
        ... on Node {
          ... on ContentfulSiteCategory {
            id: contentful_id
            categoryName
            title
            path
            image: featuredImage {
              ...FeaturedCardImage
            }
          }
        }
      }
    }
    productCategoryCardImage: contentfulSiteCategory(
      contentful_id: {eq: $contentful_id}
    ) {
      categories {
        ... on ContentfulSiteCategory {
          id
          products {
            ... on Node {
              ... on ContentfulProductContent {
                featuredImage {
                  ...FeaturedCardImage
                }
              }
              ... on ContentfulProductListingGroup {
                products {
                  ... on ContentfulProductContent {
                    featuredImage {
                      ...FeaturedCardImage
                    }
                  }
                  ... on ContentfulAlternativeImage {
                    featuredImage: image {
                      ...FeaturedCardImage
                    }
                  }
                }
              }
              ... on ContentfulAlternativeImage {
                featuredImage: image {
                  ...FeaturedCardImage
                }
              }
            }
          }
        }
      }
    }
  }
`;

const ProductCategoryPage = ({ data, pageContext }: PageProps<ProductListingPage, IPageContext>) => {
  const { productCategory } = data;
  const {
    shopInShopContent,
    categoryName,
  } = productCategory;
  const showShopInShop = Boolean(shopInShopContent);

  return (
    <Layout localizedPaths={pageContext.localizedPaths}>
      <Container className="pd-mt-6">
        <Breadcrumb>
          <Breadcrumb.Item isCurrentPage>
            {categoryName}
          </Breadcrumb.Item>
        </Breadcrumb>
      </Container>
      <CategoryPage data={data} showShopInShop={showShopInShop} />
    </Layout>
  );
};

export const Head = ({ data, pageContext }: PageProps<ProductListingPage, IPageContext>) => {
  const content = data.productCategory;

  return (
    <SEO
      locale={pageContext.locale}
      title={content.seoTitle}
      description={content.seoDescription?.seoDescription}
      robots={content.seoRobots?.seoRobots}
      alternates={pageContext.alternates}
      image={content.openGraphImage?.url}
    />
  );
};

export default ProductCategoryPage;
